import { Appointment } from "../models/admissions.model";
import { getWorksheetClaimStatusColumnDisplay } from "./worksheet-biller-columns.util";
import {
  ADMISSIONS_SCHEDULE_STATUS_OPTIONS,
  resolveAdmissionsScheduleStatusFromAppointment,
} from "./admissions-schedule-status.util";
import {
  resolveScribeSignReqStatus,
  ScribeSignReqStatus,
} from "./scribe-sign-req-status.util";
import { isValidUuid } from "./uuid.helper";
import { dobCalendarDayKey } from "../utils/dob-display.util";
import { DateTime } from "luxon";

export type ScribeSortColumn =
  | "dos"
  | "name"
  | "dob"
  | "service"
  | "provider"
  | "reasonForVisit"
  | "noteStatus"
  | "signReq"
  | "claimStatus";

export type ScribeSortDirection = "ASC" | "DESC";

export interface ScribeListSortState {
  column: ScribeSortColumn;
  direction: ScribeSortDirection;
}

export interface ScribeClientColumnFilters {
  /** @deprecated Prefer dobDayKeys (checkbox list). Kept for older persisted drafts. */
  dobStart?: string;
  /** @deprecated Prefer dobDayKeys (checkbox list). Kept for older persisted drafts. */
  dobEnd?: string;
  /** Selected DOB calendar days `yyyy-MM-dd` (Excel-style column filter). */
  dobDayKeys?: string[];
  /** Selected DOS calendar days `yyyy-MM-dd` (Excel-style column filter). */
  dosDayKeys?: string[];
  /** Selected patient name keys (`n:normalized name`) for Name column Filter by. */
  nameKeys?: string[];
  /** Selected service display names for Service column Filter by. */
  serviceKeys?: string[];
  /** Selected resource display names for Resource column Filter by. */
  resourceKeys?: string[];
  /** Selected schedule/note status values for Note Status column Filter by. */
  scheduleStatusKeys?: string[];
  /** Selected claim status display labels for Claim Status column Filter by. */
  claimStatusKeys?: string[];
  reasonContains?: string;
  signReqStatuses?: ScribeSignReqStatus[];
}

const SCRIBE_NOTE_STATUS_ORDER: Record<string, number> = Object.fromEntries(
  ADMISSIONS_SCHEDULE_STATUS_OPTIONS.map((opt, index) => [opt.value, index + 1])
);

const SCRIBE_SIGN_REQ_ORDER: Partial<Record<ScribeSignReqStatus, number>> = {
  Unsigned: 1,
  "Partially Signed": 2,
  Signed: 3,
};

export function scribeColumnUsesServerSort(column: ScribeSortColumn): boolean {
  return column === "dos";
}

export function scribeSortFieldForApi(column: ScribeSortColumn): string {
  if (column === "dos") {
    return "encounterDateTime";
  }
  return "encounterDateTime";
}

function compareStrings(a: string, b: string): number {
  return a.localeCompare(b, undefined, { sensitivity: "base" });
}

function scribePatientName(appt: Appointment): string {
  return (appt.encountersPatient?.name ?? "").trim();
}

function scribeNameFilterKeyFromDisplayName(displayName: string): string {
  const name = (displayName ?? "").trim() || "—";
  return `n:${name.toLowerCase().replace(/\s+/g, " ")}`;
}

function scribePatientNameKey(appt: Appointment): string {
  return scribeNameFilterKeyFromDisplayName(scribePatientName(appt));
}

function scribePatientDob(appt: Appointment): string {
  return (appt.encountersPatient?.dob ?? "").trim();
}

function scribeDosDayKey(appt: Appointment): string | null {
  const enc = appt.encounterDateTime;
  if (enc == null || !String(enc).trim()) {
    return null;
  }
  const ed = DateTime.fromISO(String(enc), { zone: "local" });
  return ed.isValid ? ed.toFormat("yyyy-MM-dd") : null;
}

function scribeServiceLabel(appt: Appointment): string {
  return (appt.service ?? appt.encounterProcedure?.procedureType ?? "").trim();
}

/** Scribe Provider column: schedule resource name (not physician). */
export interface ScribeScheduleResourceLookup {
  resources?: ReadonlyArray<{ id?: string; name?: string }>;
  users?: ReadonlyArray<{ id?: string; firstName?: string; lastName?: string }>;
}

function normalizeScribeScheduleResourceLookup(
  lookup: ScribeScheduleResourceLookup | undefined
): ScribeScheduleResourceLookup {
  return lookup ?? {};
}

function normalizeResourceLookupKey(value: string): string {
  return value.trim().toLowerCase();
}

function isUuidLike(value: string): boolean {
  return isValidUuid(value);
}

function collectScheduleResourceIdCandidates(appt: Appointment): string[] {
  const raw = appt as Appointment & Record<string, unknown>;
  const values = [
    appt.resourceName,
    appt.scheduleResourceId,
    appt.resourceId,
    appt.encounterSchedule?.scheduleResourceId,
    appt.providerId,
    raw["scheduleResource"],
    raw["scheduleResourceId"],
  ];

  const seen = new Set<string>();
  const ids: string[] = [];
  for (const value of values) {
    const trimmed = (value ?? "").toString().trim();
    if (!trimmed) {
      continue;
    }
    const key = normalizeResourceLookupKey(trimmed);
    if (seen.has(key)) {
      continue;
    }
    seen.add(key);
    ids.push(trimmed);
  }
  return ids;
}

function lookupScheduleResourceName(
  id: string,
  resources: ReadonlyArray<{ id?: string; name?: string }>
): string {
  if (!id || !resources.length) {
    return "";
  }
  const key = normalizeResourceLookupKey(id);
  const match = resources.find(
    (resource) => normalizeResourceLookupKey(resource.id ?? "") === key
  );
  return (match?.name ?? "").trim();
}

function lookupScheduleResourceUserName(
  id: string,
  users: ReadonlyArray<{ id?: string; firstName?: string; lastName?: string }>
): string {
  if (!id || !users.length) {
    return "";
  }
  const key = normalizeResourceLookupKey(id);
  const match = users.find(
    (user) => normalizeResourceLookupKey(user.id ?? "") === key
  );
  if (!match) {
    return "";
  }
  return `${match.firstName ?? ""} ${match.lastName ?? ""}`.trim();
}

/** Scribe Provider column: schedule resource name (not physician). */
export function resolveScribeScheduleResourceDisplay(
  appt: Appointment | null | undefined,
  lookup: ScribeScheduleResourceLookup = {}
): string {
  if (!appt) {
    return "";
  }

  const { resources = [], users = [] } =
    normalizeScribeScheduleResourceLookup(lookup);

  const resourceNameRaw = (appt.resourceName ?? "").trim();
  if (resourceNameRaw && !isUuidLike(resourceNameRaw)) {
    return resourceNameRaw;
  }

  for (const id of collectScheduleResourceIdCandidates(appt)) {
    const fromResources = lookupScheduleResourceName(id, resources);
    if (fromResources) {
      return fromResources;
    }
    const fromUsers = lookupScheduleResourceUserName(id, users);
    if (fromUsers) {
      return fromUsers;
    }
  }

  return "";
}

function scribeProviderLabel(
  appt: Appointment,
  resourceLookup?: ScribeScheduleResourceLookup
): string {
  return resolveScribeScheduleResourceDisplay(appt, resourceLookup);
}

function scribeReasonForVisit(appt: Appointment): string {
  const raw = appt as Appointment & {
    chiefComplaint?: Array<{ description?: string }>;
  };
  return (
    appt.reasonOfVisit ??
    appt.reasonForVisit ??
    raw.chiefComplaint?.[0]?.description ??
    ""
  )
    .toString()
    .trim();
}

function scribeNoteStatus(appt: Appointment): string {
  return resolveAdmissionsScheduleStatusFromAppointment(appt);
}

function scribeClaimStatus(appt: Appointment): string {
  return getWorksheetClaimStatusColumnDisplay(appt);
}

export function sortScribeAppointments(
  rows: Appointment[],
  sort: ScribeListSortState,
  resourceLookup?: ScribeScheduleResourceLookup
): Appointment[] {
  const dir = sort.direction === "ASC" ? 1 : -1;
  const sorted = [...rows];

  sorted.sort((left, right) => {
    let cmp = 0;
    switch (sort.column) {
      case "dos":
        cmp = compareStrings(
          left.encounterDateTime ?? "",
          right.encounterDateTime ?? ""
        );
        break;
      case "name":
        cmp = compareStrings(scribePatientName(left), scribePatientName(right));
        break;
      case "dob":
        cmp = compareStrings(scribePatientDob(left), scribePatientDob(right));
        break;
      case "service":
        cmp = compareStrings(scribeServiceLabel(left), scribeServiceLabel(right));
        break;
      case "provider":
        cmp = compareStrings(
          scribeProviderLabel(left, resourceLookup),
          scribeProviderLabel(right, resourceLookup)
        );
        break;
      case "reasonForVisit":
        cmp = compareStrings(
          scribeReasonForVisit(left),
          scribeReasonForVisit(right)
        );
        break;
      case "noteStatus": {
        const leftRank = SCRIBE_NOTE_STATUS_ORDER[scribeNoteStatus(left)] ?? 99;
        const rightRank = SCRIBE_NOTE_STATUS_ORDER[scribeNoteStatus(right)] ?? 99;
        cmp = leftRank - rightRank;
        if (cmp === 0) {
          cmp = compareStrings(scribeNoteStatus(left), scribeNoteStatus(right));
        }
        break;
      }
      case "signReq": {
        const leftStatus = resolveScribeSignReqStatus(left);
        const rightStatus = resolveScribeSignReqStatus(right);
        const leftRank = SCRIBE_SIGN_REQ_ORDER[leftStatus] ?? 0;
        const rightRank = SCRIBE_SIGN_REQ_ORDER[rightStatus] ?? 0;
        cmp = leftRank - rightRank;
        if (cmp === 0) {
          cmp = compareStrings(leftStatus, rightStatus);
        }
        break;
      }
      case "claimStatus":
        cmp = compareStrings(
          scribeClaimStatus(left),
          scribeClaimStatus(right)
        );
        break;
      default:
        cmp = 0;
    }
    return cmp * dir;
  });

  return sorted;
}

export function applyScribeClientColumnFilters(
  rows: Appointment[],
  filters: ScribeClientColumnFilters
): Appointment[] {
  let next = [...rows];
  const dobDayKeys = (filters.dobDayKeys ?? [])
    .map((k) => (k ?? "").trim())
    .filter(Boolean);
  const dosDayKeys = (filters.dosDayKeys ?? [])
    .map((k) => (k ?? "").trim())
    .filter(Boolean);
  const nameKeys = (filters.nameKeys ?? [])
    .map((k) => (k ?? "").trim())
    .filter(Boolean);
  const dobStart = (filters.dobStart ?? "").trim();
  const dobEnd = (filters.dobEnd ?? "").trim();
  const reasonContains = (filters.reasonContains ?? "").trim().toLowerCase();
  const signReqStatuses = filters.signReqStatuses ?? [];

  if (nameKeys.length) {
    const allowed = new Set(
      nameKeys.map((k) =>
        k.startsWith("n:")
          ? k
          : scribeNameFilterKeyFromDisplayName(k)
      )
    );
    next = next.filter((row) => allowed.has(scribePatientNameKey(row)));
  }

  if (dobDayKeys.length) {
    const allowed = new Set(dobDayKeys);
    next = next.filter((row) => {
      const dob = dobCalendarDayKey(scribePatientDob(row));
      return !!dob && allowed.has(dob);
    });
  } else {
    if (dobStart) {
      next = next.filter((row) => {
        const dob = scribePatientDob(row).slice(0, 10);
        return dob && dob >= dobStart;
      });
    }
    if (dobEnd) {
      next = next.filter((row) => {
        const dob = scribePatientDob(row).slice(0, 10);
        return dob && dob <= dobEnd;
      });
    }
  }

  if (dosDayKeys.length) {
    const allowed = new Set(dosDayKeys);
    next = next.filter((row) => {
      const day = scribeDosDayKey(row);
      return !!day && allowed.has(day);
    });
  }

  if (reasonContains) {
    next = next.filter((row) =>
      scribeReasonForVisit(row).toLowerCase().includes(reasonContains)
    );
  }
  if (signReqStatuses.length) {
    const allowed = new Set(signReqStatuses);
    next = next.filter((row) =>
      allowed.has(resolveScribeSignReqStatus(row))
    );
  }

  return next;
}

export function scribeColumnFilterIsActive(
  column: ScribeSortColumn,
  sort: ScribeListSortState,
  clientFilters: ScribeClientColumnFilters,
  appliedFilterFlags: {
    dos: boolean;
    provider: boolean;
    service: boolean;
    noteStatus: boolean;
    claimStatus: boolean;
    nameSearch: boolean;
  }
): boolean {
  if (sort.column === column) {
    return true;
  }
  switch (column) {
    case "dos":
      return (
        appliedFilterFlags.dos || (clientFilters.dosDayKeys?.length ?? 0) > 0
      );
    case "provider":
      return appliedFilterFlags.provider;
    case "service":
      return appliedFilterFlags.service;
    case "noteStatus":
      return appliedFilterFlags.noteStatus;
    case "claimStatus":
      return appliedFilterFlags.claimStatus;
    case "name":
      return (
        appliedFilterFlags.nameSearch || (clientFilters.nameKeys?.length ?? 0) > 0
      );
    case "dob":
      return (
        (clientFilters.dobDayKeys?.length ?? 0) > 0 ||
        !!(clientFilters.dobStart || clientFilters.dobEnd)
      );
    case "reasonForVisit":
      return !!clientFilters.reasonContains?.trim();
    case "signReq":
      return (clientFilters.signReqStatuses?.length ?? 0) > 0;
    default:
      return false;
  }
}
